import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Scale, Users2, Gavel, Factory, ScrollText, Plus, Pencil, Trash2, Info, AlertTriangle,
  ClipboardList, FileWarning, FolderLock,
} from "lucide-react";
import GovernanceCanonical from "@/components/GovernanceCanonical";
import GovernancePolicies from "@/components/GovernancePolicies";
import GovernanceSanctions from "@/components/GovernanceSanctions";
import { useTenant } from "@/context/TenantContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AskEvaButton } from "@/components/AskEvaButton";

const GENDER_LABELS = { FEMALE: "Donna", MALE: "Uomo", OTHER: "Altro", NOT_REPORTED: "Non indicato" };
const KIND_LABELS = { CONVICTION: "Condanna", FINE: "Sanzione pecuniaria" };
const CASE_STATUS_LABELS = { DEFINITIVE: "Definitivo", NON_DEFINITIVE: "Non definitivo" };
const SECTOR_LABELS = {
  FOSSIL_FUELS: "Combustibili fossili", CHEMICALS: "Produzione di sostanze chimiche",
  CONTROVERSIAL_WEAPONS: "Armi controverse", TOBACCO: "Coltivazione/produzione di tabacco",
};
const POLICY_LABELS = {
  CODICE_ETICO: "Codice etico", MODELLO_231: "Modello organizzativo 231",
  ANTICORRUZIONE: "Procedure anticorruzione", WHISTLEBLOWING: "Sistema di whistleblowing",
};

const EUR = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });

function ModuleCard({ code, stats, testid }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-slate-700">Disclosure {code}</p>
          <span className="text-sm font-bold text-slate-900" data-testid={`${testid}-value`}>{stats ? `${stats.completion}%` : "—"}</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 border border-slate-200">
          <div className="h-full rounded-full bg-green-700" style={{ width: `${stats?.completion || 0}%` }} />
        </div>
        {stats && (
          <p className="text-xs text-slate-500 mt-2">
            {stats.completed} completati · {stats.missing} mancanti · {stats.approved + stats.verified} verificati/approvati
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function RecordTable({ cols, rows, onEdit, onDelete, canWrite, testid, emptyText }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead>
          <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
            {cols.map((c) => <th key={c.key} className="py-2 pr-3 font-semibold">{c.label}</th>)}
            {canWrite && <th className="py-2 font-semibold w-20">Azioni</th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={cols.length + 1} className="py-4 text-xs text-slate-500">{emptyText}</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`${testid}-row-${r.id}`}>
              {cols.map((c) => <td key={c.key} className="py-2 pr-3 text-slate-800">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
              {canWrite && (
                <td className="py-2">
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(r)}
                      data-testid={`${testid}-edit-${r.id}`} aria-label="Modifica">
                      <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-700" onClick={() => onDelete(r)}
                      data-testid={`${testid}-delete-${r.id}`} aria-label="Elimina">
                      <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                    </Button>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Governance() {
  const { companyId, period, can } = useTenant();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null); // {type: 'member'|'case'|'activity', record?}
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const canWrite = can("esg:write") && period?.status !== "LOCKED";

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/governance/summary`);
      setData(data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  const mutate = async (fn, okMsg) => {
    setSaving(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      setDialog(null);
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const base = `/companies/${companyId}/periods/${period?.id}/governance`;

  const saveSingleton = (key, payload, msg) =>
    mutate(() => api.put(`${base}/${key}`, { data: payload }), msg);

  const saveRecord = () => {
    const { type, record } = dialog;
    const key = { member: "members", case: "corruption-cases", activity: "excluded-activities" }[type];
    const payload = { ...form };
    if (payload.revenues !== undefined && payload.revenues !== "") payload.revenues = Number(payload.revenues);
    if (payload.amount !== undefined && payload.amount !== "") payload.amount = Number(payload.amount);
    return mutate(() => record
      ? api.put(`${base}/${key}/${record.id}`, { data: payload })
      : api.post(`${base}/${key}`, { data: payload }),
      "Salvato. I datapoint VSME sono stati ricalcolati.");
  };

  const deleteRecord = (type, record) => {
    const key = { member: "members", case: "corruption-cases", activity: "excluded-activities" }[type];
    return mutate(() => api.delete(`${base}/${key}/${record.id}`), "Record eliminato e datapoint ricalcolati.");
  };

  const openDialog = (type, record) => {
    setDialog({ type, record });
    setForm(record ? { ...record } : {});
  };

  const savePolicy = (policyType, adopted, notes) =>
    mutate(() => api.post(`${base}/conduct-policies`, {
      data: { policy_type: policyType, adopted, notes: notes || "" },
    }), null);

  if (!period) {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center" data-testid="governance-no-period">
        <Scale className="w-10 h-10 text-slate-400 mx-auto mb-4" aria-hidden="true" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Nessun periodo attivo</h2>
        <p className="text-sm text-slate-600">Crea un periodo di rendicontazione per gestire la governance.</p>
      </div>
    );
  }
  if (error) return <p className="text-sm text-red-700" data-testid="governance-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-600" data-testid="governance-loading">Caricamento Governance Engine…</p>;

  const { body, corruption, excluded, conduct, modules, alerts, missing } = data;

  return (
    <div className="space-y-6" data-testid="governance-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Scale className="w-7 h-7 text-green-800" aria-hidden="true" />
            Governance Engine
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Disclosure B11, C8, C9 e condotta d'impresa. I dati inseriti qui calcolano automaticamente
            i datapoint VSME e alimentano il Questionario MEF - Banche.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AskEvaButton question="Perché il mio VSME non è completo? Cosa manca?" />
          <Link to="/pmi-banche" className="text-xs font-semibold text-green-700 hover:underline" data-testid="governance-goto-mef">
            Vai al Questionario MEF - Banche →
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ModuleCard code="B11" stats={modules?.B11} testid="gov-module-b11" />
        <ModuleCard code="C8" stats={modules?.C8} testid="gov-module-c8" />
        <ModuleCard code="C9" stats={modules?.C9} testid="gov-module-c9" />
      </div>

      {alerts?.length > 0 && (
        <div className="space-y-2" data-testid="governance-alerts">
          {alerts.map((a) => (
            <div key={a.message} className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-xs text-amber-900">{a.message}</p>
            </div>
          ))}
        </div>
      )}
      {missing?.length > 0 && (
        <div className="space-y-2" data-testid="governance-missing">
          {missing.map((m) => (
            <div key={`${m.module}-${m.message}`} className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3">
              <Info className="w-4 h-4 text-sky-800 shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-xs text-sky-900"><strong>{m.module}</strong> — {m.message}</p>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="body">
        <TabsList className="flex-wrap h-auto" data-testid="governance-tabs">
          <TabsTrigger value="aree" data-testid="tab-aree"><ClipboardList className="w-4 h-4 mr-1.5" aria-hidden="true" />Aree di governance</TabsTrigger>
          <TabsTrigger value="body" data-testid="tab-body"><Users2 className="w-4 h-4 mr-1.5" aria-hidden="true" />Organo di governance</TabsTrigger>
          <TabsTrigger value="corruption" data-testid="tab-corruption"><Gavel className="w-4 h-4 mr-1.5" aria-hidden="true" />Anticorruzione</TabsTrigger>
          <TabsTrigger value="excluded" data-testid="tab-excluded"><Factory className="w-4 h-4 mr-1.5" aria-hidden="true" />Attività escluse</TabsTrigger>
          <TabsTrigger value="sanzioni" data-testid="tab-sanzioni"><FileWarning className="w-4 h-4 mr-1.5" aria-hidden="true" />Sanzioni</TabsTrigger>
          <TabsTrigger value="policies" data-testid="tab-policies"><FolderLock className="w-4 h-4 mr-1.5" aria-hidden="true" />Policy Library</TabsTrigger>
          <TabsTrigger value="conduct" data-testid="tab-conduct"><ScrollText className="w-4 h-4 mr-1.5" aria-hidden="true" />Condotta d'impresa</TabsTrigger>
        </TabsList>

        <TabsContent value="aree">
          <GovernanceCanonical onChanged={load} />
        </TabsContent>

        <TabsContent value="sanzioni">
          <Card className="border-slate-200" data-testid="panel-sanzioni">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Sanzioni e violazioni del periodo</CardTitle>
            </CardHeader>
            <CardContent>
              <GovernanceSanctions data={data} onChanged={load} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="policies">
          <GovernancePolicies onChanged={load} />
        </TabsContent>

        <TabsContent value="body">
          <Card className="border-slate-200" data-testid="panel-body">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">C9 — Organo di governance e diversità di genere</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label htmlFor="gov-body-exists">L'azienda ha un organo di governance?</Label>
                  <Switch id="gov-body-exists" data-testid="gov-body-exists"
                    checked={!!body?.exists} disabled={!canWrite}
                    onCheckedChange={(v) => saveSingleton("body", { exists: v, name: body?.name || "" },
                      v ? "Organo di governance dichiarato" : "Nessun organo: C9 membri/% donne non applicabili")} />
                </div>
                {body?.exists && (
                  <div className="flex items-center gap-2">
                    <Label htmlFor="gov-body-name">Nome organo</Label>
                    <Input id="gov-body-name" data-testid="gov-body-name" className="w-64 h-9"
                      defaultValue={body?.name || ""} disabled={!canWrite}
                      onBlur={(e) => e.target.value !== (body?.name || "") &&
                        saveSingleton("body", { exists: true, name: e.target.value }, null)} />
                  </div>
                )}
              </div>

              {body?.exists && (
                <>
                  <div className="flex flex-wrap items-center gap-6 rounded-lg bg-slate-50 border border-slate-200 p-4" data-testid="gov-body-kpis">
                    <div><p className="text-xs text-slate-500">Membri</p>
                      <p className="text-2xl font-bold text-slate-900" data-testid="gov-members-count">{body.members_count ?? 0}</p></div>
                    <div><p className="text-xs text-slate-500">% donne (calcolata)</p>
                      <p className="text-2xl font-bold text-green-800" data-testid="gov-women-pct">
                        {body.women_pct !== null && body.women_pct !== undefined ? `${body.women_pct}%` : "—"}</p></div>
                    {body.calc && (
                      <p className="text-xs text-slate-500 ml-auto" data-testid="gov-women-calc">
                        Formula {body.calc.formula_code} · versione {body.calc.version} · stato {body.calc.status}
                      </p>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-semibold text-slate-700">Membri dell'organo</p>
                    {canWrite && (
                      <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={() => openDialog("member", null)}
                        data-testid="gov-member-add">
                        <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Aggiungi membro
                      </Button>
                    )}
                  </div>
                  <RecordTable testid="gov-members-table" canWrite={canWrite}
                    cols={[
                      { key: "role", label: "Ruolo" },
                      { key: "gender", label: "Genere", render: (r) => GENDER_LABELS[r.gender] || r.gender },
                    ]}
                    rows={body.members || []}
                    onEdit={(r) => openDialog("member", r)}
                    onDelete={(r) => deleteRecord("member", r)}
                    emptyText="Nessun membro registrato. Aggiungi i membri per calcolare la % donne." />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="corruption">
          <Card className="border-slate-200" data-testid="panel-corruption">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">B11 — Condanne e sanzioni per corruzione o concussione</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-6 rounded-lg bg-slate-50 border border-slate-200 p-4" data-testid="gov-corruption-kpis">
                <div><p className="text-xs text-slate-500">Condanne definitive</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="gov-convictions">{corruption?.declared ? corruption.convictions : "—"}</p></div>
                <div><p className="text-xs text-slate-500">Sanzioni definitive</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="gov-fines">{corruption?.declared ? EUR.format(corruption.fines_amount || 0) : "—"}</p></div>
                {corruption?.calc && (
                  <p className="text-xs text-slate-500 ml-auto" data-testid="gov-corruption-calc">
                    Formula {corruption.calc.formula_code} · v{corruption.calc.version} · {corruption.calc.status}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Switch id="gov-no-cases" data-testid="gov-no-cases" disabled={!canWrite}
                    checked={!!corruption?.no_cases_confirmed}
                    onCheckedChange={(v) => saveSingleton("corruption-register", { no_cases_confirmed: v },
                      v ? "Confermato: nessun caso nel periodo (0 è un valore valido)" : "Conferma rimossa")} />
                  <Label htmlFor="gov-no-cases">Confermo: nessuna condanna né sanzione nel periodo</Label>
                </div>
                {canWrite && (
                  <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={() => openDialog("case", null)}
                    data-testid="gov-case-add">
                    <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Registra caso
                  </Button>
                )}
              </div>
              <RecordTable testid="gov-cases-table" canWrite={canWrite}
                cols={[
                  { key: "kind", label: "Tipo", render: (r) => KIND_LABELS[r.kind] || r.kind },
                  { key: "status", label: "Stato", render: (r) => (
                    <Badge variant="outline" className={r.status === "DEFINITIVE"
                      ? "bg-green-50 text-green-800 border-green-300" : "bg-amber-50 text-amber-800 border-amber-300"}>
                      {CASE_STATUS_LABELS[r.status] || r.status}</Badge>) },
                  { key: "amount", label: "Importo", render: (r) => r.kind === "FINE" ? EUR.format(r.amount || 0) : "—" },
                  { key: "notes", label: "Note" },
                ]}
                rows={corruption?.cases || []}
                onEdit={(r) => openDialog("case", r)}
                onDelete={(r) => deleteRecord("case", r)}
                emptyText="Nessun caso registrato. I non definitivi sono esclusi dai totali B11." />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="excluded">
          <Card className="border-slate-200" data-testid="panel-excluded">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">C8 — Attività nei settori specifici dello standard</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-6 rounded-lg bg-slate-50 border border-slate-200 p-4" data-testid="gov-excluded-kpis">
                <div><p className="text-xs text-slate-500">Ricavi da attività escluse</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="gov-excluded-revenues">
                    {excluded?.involved ? EUR.format(excluded.revenues_excluded || 0) : "—"}</p></div>
                <div><p className="text-xs text-slate-500">Quota sul fatturato</p>
                  <p className="text-2xl font-bold text-green-800" data-testid="gov-excluded-pct">
                    {excluded?.revenues_pct !== null && excluded?.revenues_pct !== undefined ? `${excluded.revenues_pct}%` : "—"}</p></div>
                <div><p className="text-xs text-slate-500">Fatturato di riferimento</p>
                  <p className="text-sm font-semibold text-slate-700" data-testid="gov-excluded-fatturato">
                    {excluded?.fatturato ? EUR.format(excluded.fatturato) : "Non in anagrafica"}</p></div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Switch id="gov-none-excluded" data-testid="gov-none-excluded" disabled={!canWrite}
                    checked={!!(excluded?.declared && !excluded?.involved)}
                    onCheckedChange={(v) => saveSingleton("excluded-register", { none_confirmed: v },
                      v ? "Confermato: nessuna attività nei settori C8" : "Conferma rimossa")} />
                  <Label htmlFor="gov-none-excluded">Confermo: nessuna attività in questi settori</Label>
                </div>
                {canWrite && (
                  <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={() => openDialog("activity", null)}
                    data-testid="gov-activity-add">
                    <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Aggiungi attività
                  </Button>
                )}
              </div>
              <RecordTable testid="gov-activities-table" canWrite={canWrite}
                cols={[
                  { key: "sector", label: "Settore", render: (r) => SECTOR_LABELS[r.sector] || r.sector },
                  { key: "revenues", label: "Ricavi", render: (r) => EUR.format(r.revenues || 0) },
                  { key: "notes", label: "Note" },
                ]}
                rows={excluded?.activities || []}
                onEdit={(r) => openDialog("activity", r)}
                onDelete={(r) => deleteRecord("activity", r)}
                emptyText="Nessuna attività registrata nei settori VSME (fossili, chimica, armi, tabacco)." />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="conduct">
          <Card className="border-slate-200" data-testid="panel-conduct">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Condotta d'impresa — alimenta MEF.38 e MEF.39 del dossier banca</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-slate-600">
                Per ogni strumento indica se è adottato. Il motore compone automaticamente il testo per
                MEF.38 (codice etico, 231, anticorruzione, whistleblowing) e imposta MEF.39 (segnalazioni).
              </p>
              {Object.entries(POLICY_LABELS).map(([code, label]) => {
                const p = conduct?.policies?.[code];
                return (
                  <div key={code} className="flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 p-4"
                    data-testid={`gov-policy-${code}`}>
                    <div className="flex items-center gap-2 min-w-[260px]">
                      <Switch checked={!!p?.adopted} disabled={!canWrite}
                        onCheckedChange={(v) => savePolicy(code, v, p?.notes || "")}
                        data-testid={`gov-policy-switch-${code}`} aria-label={label} />
                      <span className="text-sm font-medium text-slate-900">{label}</span>
                      {p && (
                        <Badge variant="outline" className={p.adopted
                          ? "bg-green-50 text-green-800 border-green-300" : "bg-slate-50 text-slate-500 border-slate-300"}>
                          {p.adopted ? "Adottato" : "Non adottato"}</Badge>
                      )}
                    </div>
                    <Input className="flex-1 min-w-[200px] h-9" placeholder="Note (opzionale)"
                      defaultValue={p?.notes || ""} disabled={!canWrite}
                      data-testid={`gov-policy-notes-${code}`}
                      onBlur={(e) => p && e.target.value !== (p?.notes || "") &&
                        savePolicy(code, !!p?.adopted, e.target.value)} />
                  </div>
                );
              })}
              {conduct?.declared && (
                <p className="text-xs text-green-800" data-testid="gov-conduct-count">
                  {conduct.adopted_count} strumenti adottati su 4 — dossier banca aggiornato automaticamente.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent data-testid="gov-record-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {dialog?.record ? "Modifica" : "Nuovo"}{" "}
              {{ member: "membro", case: "caso", activity: "attività" }[dialog?.type]}
            </DialogTitle>
            <DialogDescription className="sr-only">Inserimento record governance</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {dialog?.type === "member" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="gov-f-role">Ruolo *</Label>
                  <Input id="gov-f-role" data-testid="gov-f-role" value={form.role || ""}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    placeholder="Es. Presidente, Consigliere" />
                </div>
                <div className="space-y-1.5">
                  <Label>Genere *</Label>
                  <Select value={form.gender || undefined} onValueChange={(v) => setForm({ ...form, gender: v })}>
                    <SelectTrigger data-testid="gov-f-gender"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(GENDER_LABELS).map(([v, l]) => (
                        <SelectItem key={v} value={v} data-testid={`gov-f-gender-${v}`}>{l}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {dialog?.type === "case" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Tipo *</Label>
                    <Select value={form.kind || undefined} onValueChange={(v) => setForm({ ...form, kind: v })}>
                      <SelectTrigger data-testid="gov-f-kind"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(KIND_LABELS).map(([v, l]) => (
                          <SelectItem key={v} value={v} data-testid={`gov-f-kind-${v}`}>{l}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Stato *</Label>
                    <Select value={form.status || undefined} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="gov-f-status"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(CASE_STATUS_LABELS).map(([v, l]) => (
                          <SelectItem key={v} value={v} data-testid={`gov-f-status-${v}`}>{l}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {form.kind === "FINE" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="gov-f-amount">Importo (EUR) *</Label>
                    <Input id="gov-f-amount" data-testid="gov-f-amount" type="number" step="any" min="0"
                      value={form.amount ?? ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="gov-f-notes">Note</Label>
                  <Textarea id="gov-f-notes" data-testid="gov-f-notes" rows={2} value={form.notes || ""}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </>
            )}
            {dialog?.type === "activity" && (
              <>
                <div className="space-y-1.5">
                  <Label>Settore *</Label>
                  <Select value={form.sector || undefined} onValueChange={(v) => setForm({ ...form, sector: v })}>
                    <SelectTrigger data-testid="gov-f-sector"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(SECTOR_LABELS).map(([v, l]) => (
                        <SelectItem key={v} value={v} data-testid={`gov-f-sector-${v}`}>{l}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="gov-f-revenues">Ricavi (EUR) *</Label>
                  <Input id="gov-f-revenues" data-testid="gov-f-revenues" type="number" step="any" min="0"
                    value={form.revenues ?? ""} onChange={(e) => setForm({ ...form, revenues: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="gov-f-anotes">Note</Label>
                  <Textarea id="gov-f-anotes" data-testid="gov-f-anotes" rows={2} value={form.notes || ""}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} data-testid="gov-f-cancel">Annulla</Button>
            <Button className="bg-green-700 hover:bg-green-800" disabled={saving} onClick={saveRecord}
              data-testid="gov-f-save">{saving ? "Salvataggio…" : "Salva e ricalcola"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
